// Workflow Editor Component
// Full CRUD editor for AI workflows, steps, and prompt mappings

import { useState, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { apiClient } from '@/lib/api/client'
import {
  Workflow,
  ChevronLeft,
  AlertCircle,
  Loader2,
  Sparkles,
  CheckCircle,
  Pause,
  ArrowRight,
  ArrowUp,
  ArrowDown,
  MessageSquare,
  Hash,
  Settings2,
  FileText,
  Users,
  AlertTriangle,
  Lightbulb,
  Briefcase,
  Bot,
  Plus,
  Trash2,
  Save,
  Edit2,
  X,
  Link,
  Unlink,
  GripVertical,
} from 'lucide-react'

// ============================================================================
// TYPES
// ============================================================================

interface StepPrompt {
  promptId: string
  promptKey: string
  promptName: string
  executionOrder: number
  isRequired: boolean
  condition: Record<string, unknown> | null
}

interface WorkflowStep {
  id: string
  key: string
  name: string
  description: string | null
  stepNumber: number
  icon: string
  color: string | null
  triggerType: string
  triggerCondition: Record<string, unknown> | null
  isActive: boolean
  prompts: StepPrompt[]
}

interface WorkflowWithSteps {
  id: string
  key: string
  name: string
  description: string | null
  icon: string
  color: string
  isActive: boolean
  isSystem: boolean
  sortOrder: number
  steps: WorkflowStep[]
}

interface AIPrompt {
  id: string
  key: string
  name: string
  category: string
  isActive: boolean
}

interface WorkflowEditorProps {
  workflowKey: string
  onClose: () => void
  onSelectPrompt?: (promptKey: string) => void
}

// ============================================================================
// ICON HELPER
// ============================================================================

function getIcon(iconName: string, className: string = 'w-4 h-4') {
  const icons: Record<string, React.ReactNode> = {
    compass: <Sparkles className={className} />,
    'graduation-cap': <Sparkles className={className} />,
    'file-text': <FileText className={className} />,
    users: <Users className={className} />,
    'alert-triangle': <AlertTriangle className={className} />,
    sparkles: <Sparkles className={className} />,
    lightbulb: <Lightbulb className={className} />,
    briefcase: <Briefcase className={className} />,
    'message-square': <MessageSquare className={className} />,
    'book-open': <FileText className={className} />,
    'message-circle': <MessageSquare className={className} />,
    'bar-chart-2': <Settings2 className={className} />,
    bot: <Bot className={className} />,
    calendar: <Settings2 className={className} />,
    search: <Settings2 className={className} />,
    workflow: <Workflow className={className} />,
  }
  return icons[iconName] || <Settings2 className={className} />
}

const TRIGGER_TYPES = [
  { value: 'on_enter', label: 'On Phase Enter' },
  { value: 'on_action', label: 'On Action' },
  { value: 'on_complete', label: 'On Complete' },
  { value: 'on_schedule', label: 'Scheduled' },
  { value: 'on_event', label: 'On Event' },
  { value: 'on_request', label: 'On Request' },
]

// ============================================================================
// API FUNCTIONS
// ============================================================================

async function fetchWorkflowFull(key: string): Promise<WorkflowWithSteps> {
  const response = await apiClient.get<{ workflow: WorkflowWithSteps }>(`/api/workflows/${key}/full`)
  return response.data.workflow
}

async function fetchAllPrompts(): Promise<AIPrompt[]> {
  const response = await apiClient.get<{ prompts: AIPrompt[] }>('/api/prompts')
  return response.data.prompts
}

async function updateWorkflow(id: string, data: Partial<WorkflowWithSteps>) {
  const response = await apiClient.put<{ workflow: WorkflowWithSteps }>(`/api/workflows/${id}`, data)
  return response.data.workflow
}

async function createStep(workflowId: string, data: {
  key: string
  name: string
  description?: string
  stepNumber: number
  triggerType?: string
}) {
  const response = await apiClient.post<{ step: WorkflowStep }>(`/api/workflows/${workflowId}/steps`, data)
  return response.data.step
}

async function updateStep(stepId: string, data: Partial<WorkflowStep>) {
  const response = await apiClient.put<{ step: WorkflowStep }>(`/api/workflows/steps/${stepId}`, data)
  return response.data.step
}

async function deleteStep(stepId: string) {
  await apiClient.delete(`/api/workflows/steps/${stepId}`)
}

async function reorderSteps(workflowId: string, steps: { stepId: string; stepNumber: number }[]) {
  await apiClient.post(`/api/workflows/${workflowId}/reorder`, { steps })
}

async function addPromptToStep(stepId: string, promptId: string, options: {
  executionOrder?: number
  isRequired?: boolean
}) {
  await apiClient.post(`/api/workflows/steps/${stepId}/prompts`, {
    promptId,
    ...options,
  })
}

async function removePromptFromStep(stepId: string, promptId: string) {
  await apiClient.delete(`/api/workflows/steps/${stepId}/prompts/${promptId}`)
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function WorkflowEditor({ workflowKey, onClose, onSelectPrompt }: WorkflowEditorProps) {
  const queryClient = useQueryClient()
  const [editingStep, setEditingStep] = useState<WorkflowStep | null>(null)
  const [showAddStep, setShowAddStep] = useState(false)
  const [showLinkPrompt, setShowLinkPrompt] = useState<string | null>(null) // stepId
  const [saving, setSaving] = useState(false)

  const {
    data: workflow,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['ai-workflow-full', workflowKey],
    queryFn: () => fetchWorkflowFull(workflowKey),
  })

  const { data: allPrompts } = useQuery({
    queryKey: ['ai-prompts-all'],
    queryFn: fetchAllPrompts,
  })

  // Mutations
  const updateWorkflowMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<WorkflowWithSteps> }) =>
      updateWorkflow(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-workflow-full', workflowKey] })
      queryClient.invalidateQueries({ queryKey: ['ai-workflows'] })
    },
  })

  const createStepMutation = useMutation({
    mutationFn: ({ workflowId, data }: { workflowId: string; data: Parameters<typeof createStep>[1] }) =>
      createStep(workflowId, data),
    onSuccess: () => {
      refetch()
      setShowAddStep(false)
    },
  })

  const updateStepMutation = useMutation({
    mutationFn: ({ stepId, data }: { stepId: string; data: Partial<WorkflowStep> }) =>
      updateStep(stepId, data),
    onSuccess: () => {
      refetch()
      setEditingStep(null)
    },
  })

  const deleteStepMutation = useMutation({
    mutationFn: deleteStep,
    onSuccess: () => refetch(),
  })

  const reorderMutation = useMutation({
    mutationFn: ({ workflowId, steps }: { workflowId: string; steps: { stepId: string; stepNumber: number }[] }) =>
      reorderSteps(workflowId, steps),
    onSuccess: () => refetch(),
  })

  const addPromptMutation = useMutation({
    mutationFn: ({ stepId, promptId, options }: { stepId: string; promptId: string; options: { executionOrder?: number; isRequired?: boolean } }) =>
      addPromptToStep(stepId, promptId, options),
    onSuccess: () => {
      refetch()
      setShowLinkPrompt(null)
    },
  })

  const removePromptMutation = useMutation({
    mutationFn: ({ stepId, promptId }: { stepId: string; promptId: string }) =>
      removePromptFromStep(stepId, promptId),
    onSuccess: () => refetch(),
  })

  // Handlers
  const handleToggleActive = useCallback(async () => {
    if (!workflow) return
    setSaving(true)
    try {
      await updateWorkflowMutation.mutateAsync({
        id: workflow.id,
        data: { isActive: !workflow.isActive },
      })
    } finally {
      setSaving(false)
    }
  }, [workflow, updateWorkflowMutation])

  const handleMoveStep = useCallback(async (stepIndex: number, direction: 'up' | 'down') => {
    if (!workflow) return
    const steps = [...workflow.steps]
    const newIndex = direction === 'up' ? stepIndex - 1 : stepIndex + 1
    if (newIndex < 0 || newIndex >= steps.length) return

    // Swap
    const temp = steps[stepIndex]
    steps[stepIndex] = steps[newIndex]
    steps[newIndex] = temp

    // Update step numbers (1-indexed to match seed data)
    const reordered = steps.map((s, i) => ({ stepId: s.id, stepNumber: i + 1 }))
    await reorderMutation.mutateAsync({ workflowId: workflow.id, steps: reordered })
  }, [workflow, reorderMutation])

  const handleDeleteStep = useCallback(async (stepId: string) => {
    if (!confirm('Are you sure you want to delete this step? This cannot be undone.')) return
    await deleteStepMutation.mutateAsync(stepId)
  }, [deleteStepMutation])

  const handleUnlinkPrompt = useCallback(async (stepId: string, promptId: string) => {
    await removePromptMutation.mutateAsync({ stepId, promptId })
  }, [removePromptMutation])

  if (isLoading) {
    return (
      <Card className="w-full">
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-text-muted" />
        </CardContent>
      </Card>
    )
  }

  if (error || !workflow) {
    return (
      <Card className="w-full">
        <CardContent className="flex items-center gap-2 text-solution py-8">
          <AlertCircle className="w-5 h-5" />
          <span>Failed to load workflow</span>
        </CardContent>
      </Card>
    )
  }

  // Get prompts not yet linked to a step
  const getAvailablePrompts = (stepId: string) => {
    const step = workflow.steps.find(s => s.id === stepId)
    if (!step || !allPrompts) return []
    const linkedIds = new Set(step.prompts.map(p => p.promptId))
    return allPrompts.filter(p => !linkedIds.has(p.id))
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={onClose}>
                <ChevronLeft className="w-4 h-4" />
                Back
              </Button>
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: `${workflow.color}20` }}
              >
                <span style={{ color: workflow.color }}>
                  {getIcon(workflow.icon, 'w-5 h-5')}
                </span>
              </div>
              <div>
                <CardTitle className="text-base font-semibold">{workflow.name}</CardTitle>
                <CardDescription className="text-text-muted">
                  {workflow.description || 'No description'}
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Badge variant="outline">{workflow.key}</Badge>
              {workflow.isSystem && (
                <Badge variant="outline" className="text-xs">
                  System
                </Badge>
              )}
              <div className="flex items-center gap-2">
                <span className="text-sm text-text-muted">Active</span>
                <Switch
                  checked={workflow.isActive}
                  onCheckedChange={handleToggleActive}
                  disabled={saving}
                />
              </div>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Steps Section */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Workflow className="w-4 h-4" />
                Workflow Steps
              </CardTitle>
              <CardDescription>
                {workflow.steps.length} steps - drag to reorder, click to edit
              </CardDescription>
            </div>
            <Button size="sm" onClick={() => setShowAddStep(true)}>
              <Plus className="w-4 h-4" />
              Add Step
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {workflow.steps.length === 0 ? (
            <div className="text-center py-8 text-text-muted">
              <Hash className="w-10 h-10 mx-auto mb-3 opacity-50" />
              <p className="font-medium">No steps configured</p>
              <p className="text-sm mt-1">Add steps to define the workflow journey.</p>
              <Button className="mt-4" onClick={() => setShowAddStep(true)}>
                <Plus className="w-4 h-4" />
                Add First Step
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {workflow.steps.map((step, index) => (
                <div key={step.id}>
                  {/* Step Card */}
                  <div className="flex items-start gap-3 p-4 bg-warm-50 rounded-lg border border-border-subtle">
                    {/* Reorder Controls */}
                    <div className="flex flex-col gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        disabled={index === 0}
                        onClick={() => handleMoveStep(index, 'up')}
                      >
                        <ArrowUp className="w-3 h-3" />
                      </Button>
                      <div className="flex items-center justify-center h-6 w-6">
                        <GripVertical className="w-4 h-4 text-text-muted" />
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        disabled={index === workflow.steps.length - 1}
                        onClick={() => handleMoveStep(index, 'down')}
                      >
                        <ArrowDown className="w-3 h-3" />
                      </Button>
                    </div>

                    {/* Step Number */}
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0"
                      style={{
                        backgroundColor: step.color ? `${step.color}20` : 'var(--color-warm-200)',
                        color: step.color || 'var(--color-text-muted)',
                      }}
                    >
                      {step.stepNumber}
                    </div>

                    {/* Step Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-text">{step.name}</span>
                        <Badge variant="outline" className="text-xs font-mono">
                          {step.key}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {TRIGGER_TYPES.find(t => t.value === step.triggerType)?.label || step.triggerType}
                        </Badge>
                        {!step.isActive && (
                          <Badge className="bg-warm-100 text-text-muted text-xs">
                            <Pause className="w-3 h-3 mr-1" />
                            Inactive
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-text-muted mb-3">
                        {step.description || 'No description'}
                      </p>

                      {/* Step Prompts */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <p className="text-xs font-medium text-text-secondary">
                            Linked Prompts ({step.prompts.length})
                          </p>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 text-xs"
                            onClick={() => setShowLinkPrompt(step.id)}
                          >
                            <Link className="w-3 h-3 mr-1" />
                            Link Prompt
                          </Button>
                        </div>
                        {step.prompts.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {step.prompts.map((prompt) => (
                              <div
                                key={prompt.promptId}
                                className="flex items-center gap-1.5 px-2 py-1 text-xs bg-white border border-warm-200 rounded-md group"
                              >
                                <button
                                  onClick={() => onSelectPrompt?.(prompt.promptKey)}
                                  className="flex items-center gap-1.5 hover:text-market"
                                >
                                  <MessageSquare className="w-3 h-3 text-market" />
                                  <span className="text-text">{prompt.promptName}</span>
                                </button>
                                {prompt.isRequired && (
                                  <span className="text-solution text-[10px]">*</span>
                                )}
                                <button
                                  onClick={() => handleUnlinkPrompt(step.id, prompt.promptId)}
                                  className="ml-1 text-text-muted hover:text-solution opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-text-muted italic">No prompts linked</p>
                        )}
                      </div>
                    </div>

                    {/* Step Actions */}
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => setEditingStep(step)}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-solution hover:text-solution"
                        onClick={() => handleDeleteStep(step.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Arrow to next step */}
                  {index < workflow.steps.length - 1 && (
                    <div className="flex justify-center py-2">
                      <ArrowRight className="w-4 h-4 text-text-muted rotate-90" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Step Dialog */}
      <AddStepDialog
        open={showAddStep}
        onClose={() => setShowAddStep(false)}
        onSubmit={async (data) => {
          await createStepMutation.mutateAsync({
            workflowId: workflow.id,
            data: {
              ...data,
              stepNumber: workflow.steps.length + 1, // 1-indexed
            },
          })
        }}
        isLoading={createStepMutation.isPending}
      />

      {/* Edit Step Dialog */}
      {editingStep && (
        <EditStepDialog
          step={editingStep}
          open={!!editingStep}
          onClose={() => setEditingStep(null)}
          onSubmit={async (data) => {
            await updateStepMutation.mutateAsync({
              stepId: editingStep.id,
              data,
            })
          }}
          isLoading={updateStepMutation.isPending}
        />
      )}

      {/* Link Prompt Dialog */}
      {showLinkPrompt && (
        <LinkPromptDialog
          open={!!showLinkPrompt}
          onClose={() => setShowLinkPrompt(null)}
          availablePrompts={getAvailablePrompts(showLinkPrompt)}
          onSubmit={async (promptId, options) => {
            await addPromptMutation.mutateAsync({
              stepId: showLinkPrompt,
              promptId,
              options,
            })
          }}
          isLoading={addPromptMutation.isPending}
        />
      )}
    </div>
  )
}

// ============================================================================
// ADD STEP DIALOG
// ============================================================================

interface AddStepDialogProps {
  open: boolean
  onClose: () => void
  onSubmit: (data: { key: string; name: string; description?: string; triggerType: string }) => Promise<void>
  isLoading: boolean
}

function AddStepDialog({ open, onClose, onSubmit, isLoading }: AddStepDialogProps) {
  const [key, setKey] = useState('')
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [triggerType, setTriggerType] = useState('on_enter')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    await onSubmit({ key, name, description: description || undefined, triggerType })
    setKey('')
    setName('')
    setDescription('')
    setTriggerType('on_enter')
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add New Step</DialogTitle>
          <DialogDescription>
            Create a new step in this workflow
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="key">Key (lowercase, hyphens only)</Label>
              <Input
                id="key"
                value={key}
                onChange={(e) => setKey(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                placeholder="e.g., market-research"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Market Research"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What this step does..."
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="triggerType">Trigger Type</Label>
              <Select value={triggerType} onValueChange={setTriggerType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TRIGGER_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={onClose} type="button">
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading || !key || !name}>
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Add Step
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// ============================================================================
// EDIT STEP DIALOG
// ============================================================================

interface EditStepDialogProps {
  step: WorkflowStep
  open: boolean
  onClose: () => void
  onSubmit: (data: Partial<WorkflowStep>) => Promise<void>
  isLoading: boolean
}

function EditStepDialog({ step, open, onClose, onSubmit, isLoading }: EditStepDialogProps) {
  const [name, setName] = useState(step.name)
  const [description, setDescription] = useState(step.description || '')
  const [triggerType, setTriggerType] = useState(step.triggerType)
  const [isActive, setIsActive] = useState(step.isActive)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    await onSubmit({
      name,
      description: description || undefined,
      triggerType,
      isActive,
    })
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Step</DialogTitle>
          <DialogDescription>
            Update step configuration - Key: {step.key}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Name</Label>
              <Input
                id="edit-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-description">Description</Label>
              <Textarea
                id="edit-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-triggerType">Trigger Type</Label>
              <Select value={triggerType} onValueChange={setTriggerType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TRIGGER_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="edit-active">Active</Label>
              <Switch
                id="edit-active"
                checked={isActive}
                onCheckedChange={setIsActive}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={onClose} type="button">
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading || !name}>
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save Changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// ============================================================================
// LINK PROMPT DIALOG
// ============================================================================

interface LinkPromptDialogProps {
  open: boolean
  onClose: () => void
  availablePrompts: AIPrompt[]
  onSubmit: (promptId: string, options: { executionOrder?: number; isRequired?: boolean }) => Promise<void>
  isLoading: boolean
}

function LinkPromptDialog({ open, onClose, availablePrompts, onSubmit, isLoading }: LinkPromptDialogProps) {
  const [selectedPromptId, setSelectedPromptId] = useState('')
  const [isRequired, setIsRequired] = useState(true)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    await onSubmit(selectedPromptId, { isRequired })
    setSelectedPromptId('')
    setIsRequired(true)
  }

  // Group prompts by category
  const byCategory = availablePrompts.reduce((acc, prompt) => {
    const cat = prompt.category || 'general'
    if (!acc[cat]) acc[cat] = []
    acc[cat].push(prompt)
    return acc
  }, {} as Record<string, AIPrompt[]>)

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Link Prompt to Step</DialogTitle>
          <DialogDescription>
            Select a prompt to link to this step
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            {availablePrompts.length === 0 ? (
              <p className="text-center text-text-muted py-4">
                All prompts are already linked to this step
              </p>
            ) : (
              <>
                <div className="space-y-2">
                  <Label>Select Prompt</Label>
                  <div className="max-h-64 overflow-y-auto border rounded-md">
                    {Object.entries(byCategory).map(([category, prompts]) => (
                      <div key={category}>
                        <div className="px-3 py-1.5 bg-warm-50 text-xs font-medium text-text-secondary capitalize sticky top-0">
                          {category}
                        </div>
                        {prompts.map(prompt => (
                          <button
                            key={prompt.id}
                            type="button"
                            onClick={() => setSelectedPromptId(prompt.id)}
                            className={`w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-warm-50 ${
                              selectedPromptId === prompt.id ? 'bg-market/10 border-l-2 border-market' : ''
                            }`}
                          >
                            <MessageSquare className="w-4 h-4 text-market flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{prompt.name}</p>
                              <p className="text-xs text-text-muted truncate">{prompt.key}</p>
                            </div>
                            {selectedPromptId === prompt.id && (
                              <CheckCircle className="w-4 h-4 text-market" />
                            )}
                          </button>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="link-required">Required</Label>
                  <Switch
                    id="link-required"
                    checked={isRequired}
                    onCheckedChange={setIsRequired}
                  />
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={onClose} type="button">
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading || !selectedPromptId}>
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link className="w-4 h-4" />}
              Link Prompt
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default WorkflowEditor
